import { useState, useMemo } from 'react';
import { BookCheck, XCircle, CheckCircle2, AlertTriangle, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

function formatEur(n) {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 2,
  }).format(Number(n ?? 0));
}

/**
 * Render an amount that may arrive as a number (contable previews — r2(...))
 * or as a preformatted string like "123.45€" (CREATE_JOURNAL_ENTRY). Empty
 * cell for null/0 so the Debe/Haber columns stay readable.
 */
function fmtAmount(v) {
  if (v == null || v === '') return '';
  if (typeof v === 'number') return v === 0 ? '' : formatEur(v);
  return v; // already a string with the € suffix
}

/**
 * Parse an amount that may be a number or a string like "1000.00€" into a Number.
 * The CREATE_JOURNAL_ENTRY preview emits `${x.toFixed(2)}€` (dot decimal, no
 * thousands separator), so a light strip + parseFloat is enough.
 */
function parseAmount(v) {
  if (typeof v === 'number') return v;
  if (v == null) return 0;
  const n = parseFloat(String(v).replace(/[€\s]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

/** Build the NL confirmation message for an edited entry so the tool re-posts it. */
function buildEditedMessage(fecha, descripcion, lines) {
  const parts = lines
    .filter((l) => l.cuenta && (parseAmount(l.debe) > 0 || parseAmount(l.haber) > 0))
    .map((l) => {
      const d = parseAmount(l.debe);
      const h = parseAmount(l.haber);
      return d > 0 ? `cuenta ${l.cuenta} debe ${d.toFixed(2)}` : `cuenta ${l.cuenta} haber ${h.toFixed(2)}`;
    });
  const fechaTxt = fecha ? ` con fecha ${fecha}` : '';
  return `Contabiliza este asiento${fechaTxt}, descripción "${descripcion}". Líneas: ${parts.join('; ')}.`;
}

/**
 * JournalEntryPreview — rich two-step approval card for asiento (journal entry)
 * actions emitted by ARIA. Replaces the generic key-value PendingActionCard
 * with a proper Debe/Haber table per PGC account, totals and a balance check.
 *
 * Fase 5 (UI generativa inline): el asiento es EDITABLE in situ. El usuario
 * pulsa "Editar", ajusta cuentas/importes, añade o quita líneas y ve el cuadre
 * recalcularse en vivo. Al contabilizar con cambios, se reenvía el asiento
 * ajustado a ARIA en lenguaje natural (el tool `crear_asiento_desde_nl` lo
 * reprocesa) — sin canal nuevo: reutiliza el flujo two-step existente.
 *
 * Handles BOTH backend payload shapes:
 *   - CREATE_JOURNAL_ENTRY: lines in `preview.lineas`, amounts as "123.45€" strings.
 *   - Contable posts (POST_PERIODIFICATION, ...): lines in `preview.asiento.lineas`,
 *     amounts as numbers.
 *
 * Props: action { type, preview }, onApprove (acepta mensaje editado opcional),
 *        onReject, status
 */
export default function JournalEntryPreview({ action, onApprove, onReject, status }) {
  const { preview } = action;

  // Normalize across the two payload shapes.
  const a = preview?.asiento ?? preview ?? {};
  const rawLines = a.lineas ?? preview?.lineas ?? [];
  const descripcion = a.descripcion ?? preview?.descripcion ?? '—';
  const fecha = preview?.fecha ?? a.fecha ?? null;

  // Edit state — only journal entries from NL are safely re-postable, but the
  // editor is shape-agnostic; for contable posts the user can still cancel.
  const [editing, setEditing] = useState(false);
  const [lines, setLines] = useState(() =>
    rawLines.map((l) => ({
      cuenta: String(l.cuenta ?? ''),
      nombre: l.nombre_cuenta ?? l.cuenta_nombre ?? '',
      debe: parseAmount(l.debe),
      haber: parseAmount(l.haber),
    })),
  );
  const [dirty, setDirty] = useState(false);

  const totals = useMemo(() => {
    const debe = lines.reduce((s, l) => s + parseAmount(l.debe), 0);
    const haber = lines.reduce((s, l) => s + parseAmount(l.haber), 0);
    return { debe, haber, balanced: Math.abs(debe - haber) < 0.005 };
  }, [lines]);

  if (status === 'approved') {
    return (
      <div className="flex items-center gap-2 p-2 bg-success/10 border border-success/30 rounded-lg text-xs text-success my-1">
        <BookCheck className="w-3.5 h-3.5" />
        <span>Asiento aprobado — contabilizando…</span>
      </div>
    );
  }
  if (status === 'rejected') {
    return (
      <div className="flex items-center gap-2 p-2 bg-muted border border-border rounded-lg text-xs text-muted-foreground my-1">
        <XCircle className="w-3.5 h-3.5" />
        <span>Contabilización cancelada</span>
      </div>
    );
  }

  // In read mode, prefer the backend-provided totals/balance (authoritative);
  // in edit mode, the live recomputation drives the UI.
  const totalDebe = editing || dirty ? totals.debe : (a.total_debe ?? preview?.total_debe);
  const totalHaber = editing || dirty ? totals.haber : (a.total_haber ?? preview?.total_haber);
  const cuadrado = editing || dirty ? totals.balanced : (a.cuadrado ?? preview?.cuadrado);

  const updateLine = (i, field, value) => {
    setDirty(true);
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)));
  };
  const addLine = () => { setDirty(true); setLines((prev) => [...prev, { cuenta: '', nombre: '', debe: 0, haber: 0 }]); };
  const removeLine = (i) => { setDirty(true); setLines((prev) => prev.filter((_, idx) => idx !== i)); };

  const confirm = () => {
    if (dirty) onApprove(buildEditedMessage(fecha, descripcion, lines));
    else onApprove();
  };

  const numInput = 'w-16 rounded border border-indigo-200 dark:border-indigo-800 bg-white dark:bg-slate-900 px-1 py-0.5 text-right text-[11px] tabular-nums focus:outline-none focus:ring-1 focus:ring-indigo-400';

  return (
    <div className="rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50/60 dark:bg-indigo-950/20 p-4 space-y-3 my-2 max-w-sm">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <Badge variant="outline" className="text-indigo-700 dark:text-indigo-300 border-indigo-300 dark:border-indigo-700 text-[10px] mb-1">
            Requiere aprobación
          </Badge>
          <p className="text-sm font-semibold text-foreground leading-tight">{descripcion}</p>
          {fecha && <p className="text-[10px] text-muted-foreground mt-0.5">{fecha}</p>}
        </div>
        {cuadrado != null && (
          <span className={`flex items-center gap-1 text-[10px] font-medium shrink-0 ${cuadrado ? 'text-success' : 'text-warning'}`}>
            {cuadrado
              ? <><CheckCircle2 className="w-3 h-3" />Cuadrado</>
              : <><AlertTriangle className="w-3 h-3" />Descuadrado</>}
          </span>
        )}
      </div>

      {/* Debe/Haber table */}
      {lines.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="border-b border-indigo-200 dark:border-indigo-800 text-muted-foreground">
                <th className="text-left pb-1 font-medium">Cuenta</th>
                <th className="text-right pb-1 font-medium">Debe</th>
                <th className="text-right pb-1 font-medium">Haber</th>
                {editing && <th className="w-4" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-indigo-100 dark:divide-indigo-900/40">
              {lines.map((l, i) => (
                <tr key={i}>
                  <td className="py-1 pr-2 text-foreground">
                    {editing ? (
                      <input
                        value={l.cuenta}
                        onChange={(e) => updateLine(i, 'cuenta', e.target.value)}
                        aria-label={`Cuenta línea ${i + 1}`}
                        className="w-20 rounded border border-indigo-200 dark:border-indigo-800 bg-white dark:bg-slate-900 px-1 py-0.5 font-mono text-[11px] focus:outline-none focus:ring-1 focus:ring-indigo-400"
                      />
                    ) : (
                      <>
                        <span className="font-mono tabular-nums">{l.cuenta}</span>
                        {l.nombre && (
                          <span className="block text-[10px] text-muted-foreground max-w-[150px] truncate">{l.nombre}</span>
                        )}
                      </>
                    )}
                  </td>
                  <td className="py-1 text-right tabular-nums text-foreground">
                    {editing
                      ? <input type="number" min="0" step="0.01" value={l.debe || ''} aria-label={`Debe línea ${i + 1}`} onChange={(e) => updateLine(i, 'debe', e.target.value)} className={numInput} />
                      : fmtAmount(dirty ? l.debe : (rawLines[i]?.debe ?? l.debe))}
                  </td>
                  <td className="py-1 text-right tabular-nums text-foreground">
                    {editing
                      ? <input type="number" min="0" step="0.01" value={l.haber || ''} aria-label={`Haber línea ${i + 1}`} onChange={(e) => updateLine(i, 'haber', e.target.value)} className={numInput} />
                      : fmtAmount(dirty ? l.haber : (rawLines[i]?.haber ?? l.haber))}
                  </td>
                  {editing && (
                    <td className="py-1 pl-1 text-right">
                      <button type="button" aria-label={`Quitar línea ${i + 1}`} onClick={() => removeLine(i)} className="text-muted-foreground hover:text-destructive">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            {(totalDebe != null || totalHaber != null) && (
              <tfoot>
                <tr className="border-t border-indigo-200 dark:border-indigo-800 font-semibold text-foreground">
                  <td className="pt-1.5 text-left">Total</td>
                  <td className="pt-1.5 text-right tabular-nums">{fmtAmount(totalDebe)}</td>
                  <td className="pt-1.5 text-right tabular-nums">{fmtAmount(totalHaber)}</td>
                  {editing && <td />}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {editing && (
        <button type="button" onClick={addLine} className="flex items-center gap-1 text-[11px] text-indigo-600 dark:text-indigo-400 hover:underline">
          <Plus className="w-3 h-3" /> Añadir línea
        </button>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={onReject} className="flex-1 h-7 text-xs">
          <XCircle className="w-3 h-3 mr-1" />Cancelar
        </Button>
        {!editing && (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)} className="h-7 text-xs" aria-label="Editar asiento">
            <Pencil className="w-3 h-3 mr-1" />Editar
          </Button>
        )}
        <Button size="sm" onClick={confirm} disabled={editing && !cuadrado}
          className="flex-1 h-7 text-xs bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50">
          <BookCheck className="w-3 h-3 mr-1" />Contabilizar
        </Button>
      </div>
      {editing && !cuadrado && (
        <p className="text-[10px] text-warning">El asiento debe cuadrar (Debe = Haber) para contabilizar.</p>
      )}
    </div>
  );
}
